"use client";

import { type RefObject, useEffect, useLayoutEffect, useState } from "react";

/** Matches Tailwind `lg` (992px): stacked mobile/tablet locator layout below this width. */
const LOCATOR_STACKED_LAYOUT_MEDIA = "(max-width: 991px)";

/** Wait for layout to settle before posting height to the parent iframe. */
const HEIGHT_REPORT_DEBOUNCE_MS = 100;

let heightReportTimeout: ReturnType<typeof setTimeout> | null = null;
let pendingContainerRef: RefObject<Element | null> | null = null;

function sendContainerWidgetHeight(
  containerRef: RefObject<Element | null>,
): void {
  const el = containerRef.current;
  if (!el) return;
  const height = Math.ceil(el.getBoundingClientRect().height);
  window.parent.postMessage({ containerWidgetHeight: height }, "*");
}

function cancelScheduledContainerWidgetHeightReport(): void {
  if (heightReportTimeout !== null) {
    clearTimeout(heightReportTimeout);
    heightReportTimeout = null;
  }
  pendingContainerRef = null;
}

function scheduleContainerWidgetHeightReport(
  containerRef: RefObject<Element | null>,
): void {
  pendingContainerRef = containerRef;
  if (heightReportTimeout !== null) {
    clearTimeout(heightReportTimeout);
  }
  heightReportTimeout = setTimeout(() => {
    heightReportTimeout = null;
    const ref = pendingContainerRef;
    pendingContainerRef = null;
    if (!ref) return;
    sendContainerWidgetHeight(ref);
  }, HEIGHT_REPORT_DEBOUNCE_MS);
}

export function useLocatorStackedLayout(): boolean {
  const [isStacked, setIsStacked] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(LOCATOR_STACKED_LAYOUT_MEDIA).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(LOCATOR_STACKED_LAYOUT_MEDIA);
    const onChange = () => setIsStacked(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isStacked;
}

type UseIframeResizeReporterOptions = {
  containerRef: RefObject<Element | null>;
  /** When false, observers are not attached. */
  enabled?: boolean;
  /** Schedule a debounced height report after render when these values change (e.g. result count, map toggle). */
  contentDeps?: ReadonlyArray<unknown>;
};

/**
 * Observes the container ref and posts its exact height (including its own padding) to the parent iframe.
 */
export function useIframeResizeReporter({
  containerRef,
  enabled = true,
  contentDeps = [],
}: UseIframeResizeReporterOptions): void {
  useLayoutEffect(() => {
    if (!enabled) return;

    let rafAfterPaint: number | undefined;
    const rafId = requestAnimationFrame(() => {
      rafAfterPaint = requestAnimationFrame(() => {
        scheduleContainerWidgetHeightReport(containerRef);
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (rafAfterPaint !== undefined) {
        cancelAnimationFrame(rafAfterPaint);
      }
    };
  }, [enabled, containerRef, ...contentDeps]);

  useEffect(() => {
    if (!enabled) return;

    const observed = new Set<Element>();

    const observeContainer = () => {
      const container = containerRef.current;
      if (!container || observed.has(container)) return;
      observed.add(container);
      resizeObserver.observe(container);
    };

    const reportDebounced = () =>
      scheduleContainerWidgetHeightReport(containerRef);

    const resizeObserver = new ResizeObserver(reportDebounced);

    observeContainer();

    const rafId = requestAnimationFrame(observeContainer);

    window.addEventListener("resize", reportDebounced);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", reportDebounced);
      cancelScheduledContainerWidgetHeightReport();
    };
  }, [enabled, containerRef]);
}
